import {UserStatusInfo} from '../../interfaces/userStatusInfo.interface';
import moment from 'moment';

export type UserManagementExportFormat = 'pdf' | 'xlsx';

interface UserManagementExportColumn {
  header: string;
  width: number;
  value: (user: UserStatusInfo) => string;
}

const exportColumns: UserManagementExportColumn[] = [
  {header: 'User Type', width: 24, value: (user) => user.accountType ?? ''},
  {header: 'User Name', width: 32, value: (user) => user.userName ?? ''},
  {header: 'Company/Clinic', width: 42, value: (user) => user.company ?? ''},
  {header: 'Registration', width: 18, value: (user) => formatExportDate(user.registration)},
  {header: 'Last Login', width: 18, value: (user) => formatExportDate(user.lastLogin)},
  {header: 'Status', width: 16, value: (user) => user.userStatus ?? ''}
];

export async function exportUserManagementResults(
  users: UserStatusInfo[],
  format: UserManagementExportFormat
): Promise<void> {
  const fileName = `User_Management_Export_${moment().format('YYYY-MM-DD')}`;

  if (format === 'xlsx') {
    await exportExcel(users, fileName);
    return;
  }

  await exportPdf(users, fileName);
}

async function exportExcel(users: UserStatusInfo[], fileName: string): Promise<void> {
  const {default: ExcelJS} = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('User Management');

  worksheet.addRow(exportColumns.map((column) => column.header));
  users.forEach((user) => {
    worksheet.addRow(exportColumns.map((column) => column.value(user)));
  });
  worksheet.columns.forEach((column, index) => {
    column.width = exportColumns[index].width;
  });
  worksheet.views = [{state: 'frozen', ySplit: 1}];
  worksheet.autoFilter = {
    from: {row: 1, column: 1},
    to: {row: 1, column: exportColumns.length}
  };
  worksheet.getRow(1).font = {bold: true};
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {argb: 'FF6EC6AA'}
  };

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(
    new Blob([new Uint8Array(buffer)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    `${fileName}.xlsx`
  );
}

async function exportPdf(users: UserStatusInfo[], fileName: string): Promise<void> {
  const [{jsPDF}, {default: autoTable}] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  const document = new jsPDF({orientation: 'landscape', unit: 'pt', format: 'letter'});

  document.setProperties({title: 'User Management Export'});
  autoTable(document, {
    head: [exportColumns.map((column) => column.header)],
    body: users.map((user) => exportColumns.map((column) => column.value(user))),
    startY: 58,
    margin: {top: 58, right: 24, bottom: 30, left: 24},
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 4,
      overflow: 'linebreak',
      valign: 'middle'
    },
    headStyles: {
      fillColor: [110, 198, 170],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    alternateRowStyles: {fillColor: [245, 250, 248]},
    columnStyles: {
      0: {cellWidth: 90},
      1: {cellWidth: 135},
      2: {cellWidth: 245},
      3: {cellWidth: 85},
      4: {cellWidth: 85},
      5: {cellWidth: 70}
    },
    willDrawPage: () => {
      document.setFont('helvetica', 'bold');
      document.setFontSize(16);
      document.text('User Management', 24, 34);
      document.setFont('helvetica', 'normal');
      document.setFontSize(9);
      document.text(`Exported ${moment().format('MM/DD/YYYY')}`, 24, 48);
    },
    didDrawPage: (data) => {
      document.setFont('helvetica', 'normal');
      document.setFontSize(8);
      document.text(
        `Page ${data.pageNumber}`,
        document.internal.pageSize.getWidth() - 24,
        document.internal.pageSize.getHeight() - 12,
        {align: 'right'}
      );
    }
  });

  downloadFile(document.output('blob'), `${fileName}.pdf`);
}

function formatExportDate(value: Date | string | null): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('MM/DD/YYYY') : '';
}

function downloadFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
